import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API, getToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDateTime, fmtDate, fmtNum, fmtChange, fmtPct, computePct } from "@/lib/format";
import { TrendingUp, TrendingDown, ArrowRight, Newspaper, FileText, Bell, Sparkles, AlertTriangle, Calendar, Download, Star } from "lucide-react";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import LastUpdateChip from "@/components/LastUpdateChip";
import { EaDashboardCard } from "@/components/energy-analytics/analytics/EaDashboardCard";

const INDICATOR_META = {
  "Brent Dated": { unit: "$/bbl", accent: "from-amber-500 to-orange-600" },
  "CO2-EUA IV (Spot)": { unit: "€/tCO2", accent: "from-emerald-500 to-teal-600" },
  "GME PUN D+1": { unit: "€/MWh", accent: "from-yellow-500 to-amber-600" },
  "Cambio €/$": { unit: "", accent: "from-blue-500 to-indigo-600" },
};

function KPICard({ title, value, change, changePct, unit, ts, testid, digits = 2 }) {
  // Convenzione B2B buyer: prezzo in AUMENTO = rosso (peggior costo), prezzo in DIMINUZIONE = verde (risparmio)
  const up = change != null && Number(change) > 0;
  const down = change != null && Number(change) < 0;
  // Se la % non è stata passata, la calcoliamo dal change assoluto + valore corrente
  const pct = changePct != null ? Number(changePct) : computePct(value, change);
  const pctLabel = fmtPct(pct);
  return (
    <div className="card-elevated p-5 flex flex-col gap-2" data-testid={testid}>
      <div className="flex items-center justify-between text-xs text-slate-500 font-semibold uppercase tracking-wider">
        <span>{title}</span>
        {change != null && (
          <span className={`${up ? "badge-neg" : down ? "badge-pos" : "badge-neutral"} !flex-col !items-end !py-1 !gap-0 leading-tight`}>
            <span className="flex items-center gap-1">
              {up ? <TrendingUp size={12} /> : down ? <TrendingDown size={12} /> : null}
              <span className="font-mono-nums">{fmtChange(change, digits >= 3 ? 3 : 2)}</span>
            </span>
            {pctLabel && (
              <span className={`font-mono-nums text-[10px] opacity-80 mt-0.5 pt-0.5 border-t w-full text-right ${up ? "border-red-200/70" : down ? "border-emerald-200/70" : "border-slate-200/70"}`}>
                {pctLabel}
              </span>
            )}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1.5 mt-1">
        <div className="font-display text-3xl font-bold tracking-tight text-slate-900">{fmtNum(value, digits)}</div>
        {unit && <div className="text-xs text-slate-500 font-medium">{unit}</div>}
      </div>
      <div className="text-[11px] text-slate-400 font-mono-nums mt-auto pt-2">
        {fmtDateTime(ts)}
      </div>
    </div>
  );
}

function PricesMiniCard({ title, sheet, prices }) {
  const hasPrice = (p) =>
    (p.ask != null && Number(p.ask) > 0) ||
    (p.last_price != null && Number(p.last_price) > 0) ||
    (p.previous_close != null && Number(p.previous_close) > 0);
  const all = prices.filter((p) => p.source_sheet === sheet);
  const withPrice = all.filter(hasPrice);
  const items = (withPrice.length >= 4 ? withPrice : all).slice(0, 4);
  return (
    <div className="card-elevated p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-semibold text-slate-900">{title}</div>
        <Link
          to={`/forward/${sheet.toLowerCase()}`}
          className="text-xs text-blue-600 hover:text-purple-600 flex items-center gap-1"
        >
          Vedi tutto <ArrowRight size={12} />
        </Link>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <div className="text-sm text-slate-400">Nessun dato</div>}
        {items.map((p) => {
          const pct = p.change_pct != null ? Number(p.change_pct) : computePct(p.last_price, p.change_value);
          const pctLabel = fmtPct(pct);
          return (
            <div key={p.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
              <div>
                <div className="font-medium text-slate-800">{p.contract_name}</div>
                <div className="text-[10px] text-slate-400 uppercase">{p.period_type}</div>
              </div>
              <div className="text-right">
                <div className="font-mono-nums text-slate-900 font-semibold">{fmtNum(p.last_price)}</div>
                {p.change_value != null && (
                  <div className={`text-[11px] font-mono-nums ${p.change_value > 0 ? "text-red-600" : p.change_value < 0 ? "text-emerald-600" : "text-slate-500"}`}>
                    {fmtChange(p.change_value)}
                    {pctLabel && <span className="opacity-70"> ({pctLabel})</span>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const NOTIF_TYPE_META = {
  report: { label: "Report", accent: "from-purple-500 to-indigo-600", icon: FileText },
  custom_report: { label: "Report personalizzato", accent: "from-blue-500 to-purple-600", icon: FileText },
  news: { label: "News", accent: "from-blue-500 to-sky-600", icon: Newspaper },
  briefing: { label: "Briefing", accent: "from-purple-500 to-pink-600", icon: Sparkles },
};

function NotificationItem({ n, onRead }) {
  const navigate = useNavigate();
  const rtype = n.related_resource_type;
  const rid = n.related_resource_id;
  const meta = NOTIF_TYPE_META[rtype] || { label: "Notifica", accent: "from-orange-400 to-orange-500", icon: Bell };
  const Icon = meta.icon;
  const isUnread = !n.read;

  const markRead = async () => {
    if (n.read) return;
    try {
      await api.post(`/notifications/${n.id}/read`);
      onRead?.();
    } catch (_e) { /* silent */ }
  };

  const handleClick = async (e) => {
    e.preventDefault();
    await markRead();
    if (!rid) {
      navigate("/notifiche");
      return;
    }
    if (rtype === "news") {
      navigate(`/news/${rid}`);
    } else if (rtype === "briefing") {
      navigate(`/briefing/${rid}`);
    } else if (rtype === "report") {
      const url = `${API}/reports/${rid}/download?auth=${encodeURIComponent(getToken() || "")}&disposition=attachment`;
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      a.download = `${n.message || "report"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else if (rtype === "custom_report") {
      const url = `${API}/custom-reports/${rid}/download?auth=${encodeURIComponent(getToken() || "")}`;
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      a.download = `${n.message || "report"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else {
      navigate("/notifiche");
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`group w-full text-left -mx-2 px-2 py-2 rounded-lg hover:bg-orange-50/60 transition-colors flex items-start gap-2 ${isUnread ? "bg-orange-50/30" : ""}`}
      data-testid={`dash-notif-${n.id}`}
    >
      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${meta.accent} flex items-center justify-center flex-shrink-0 shadow-sm`}>
        <Icon size={13} className="text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-widest font-bold text-orange-600">{meta.label}</span>
          {isUnread && <span className="w-1.5 h-1.5 rounded-full bg-orange-500" title="Non letta" />}
        </div>
        <div className="text-sm font-semibold text-slate-900 line-clamp-1 group-hover:text-orange-700">
          {n.message || n.title}
        </div>
        <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">{n.title} · {fmtDateTime(n.sent_at)}</div>
      </div>
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isStaffUser = user?.role === "admin" || user?.role === "editor";
  const [indicators, setIndicators] = useState([]);
  const [prices, setPrices] = useState([]);
  const [news, setNews] = useState([]);
  const [reports, setReports] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [briefing, setBriefing] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const safe = (p) => p.catch(() => ({ data: [] }));
      const [i, p, n, r, cr, nt, bf] = await Promise.all([
        safe(api.get("/dashboard/indicators")),
        safe(api.get("/prices")),
        safe(api.get("/news")),
        safe(api.get("/reports")),
        safe(api.get("/custom-reports")),
        safe(api.get("/notifications")),
        safe(api.get("/briefings")),
      ]);
      setIndicators(i.data || []);
      setPrices(p.data || []);
      // "Ultime news" in dashboard: mostriamo almeno 6 news non-in-evidenza (le news
      // in evidenza hanno il loro riquadro dedicato "News in evidenza"). Se oggi ne
      // sono state pubblicate di più, le mostriamo TUTTE (capped a 10 per il layout).
      // Nota: in state teniamo TUTTE le news; il filtering avviene al render per
      // preservare il riquadro "News in evidenza".
      const allNews = n.data || [];
      const nonFeatured = allNews.filter((x) => !x.is_featured);
      const todayStr = new Date().toISOString().slice(0, 10);
      const todayCount = nonFeatured.filter(
        (x) => (x.publication_date || x.published_at || x.created_at || "").slice(0, 10) === todayStr
      ).length;
      const limit = Math.min(10, Math.max(6, todayCount));
      // Teniamo la lista completa in state così il blocco "News in evidenza" continua
      // a trovare le featured, ma la lista "Ultime news" avrà uno slice calcolato.
      const latest = nonFeatured.slice(0, limit);
      const featured = allNews.filter((x) => x.is_featured);
      setNews([...featured, ...latest]);
      // Unifica report standard + report personalizzati, marca i custom con _isCustom,
      // ordina per data (created_at per i custom, report_date per gli standard) e prende i 3 più recenti.
      // Il backend applica già RBAC: i custom-reports sono visibili SOLO al cliente della propria azienda.
      const regular = (r.data || []).map((x) => ({ ...x, _kind: "report", _sortDate: x.report_date || x.published_at || x.created_at || "" }));
      const custom = (cr.data || []).map((x) => ({ ...x, _kind: "custom", _sortDate: x.created_at || "" }));
      const briefings = (bf.data || []).map((x) => ({ ...x, _kind: "briefing", _sortDate: x.generated_at || x.created_at || "" }));
      const merged = [...regular, ...custom, ...briefings].sort((a, b) => (b._sortDate || "").localeCompare(a._sortDate || ""));
      setReports(merged.slice(0, 5));
      setNotifs((nt.data || []).slice(0, 4));
      setBriefing((bf.data || [])[0] || null);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  // Auto-refresh 60s + immediate refresh on tab focus
  useAutoRefresh(load, 60_000);

  const findIndicator = (namePrefix) =>
    indicators.find((i) => (i.indicator_name || "").toLowerCase().includes(namePrefix.toLowerCase()));

  const brent = findIndicator("Brent");
  const co2 = findIndicator("CO2");
  const pun = findIndicator("PUN");
  const eur = findIndicator("Cambio");

  // Timestamp dell'ultimo dataset pubblicato: massimo tra imported_at di indicators + prices.
  // Rappresenta esattamente il momento in cui l'ultimo import (manuale o automatico) è andato a buon fine.
  const lastPublishedAt = React.useMemo(() => {
    let max = "";
    for (const it of [...indicators, ...prices]) {
      const ts = it?.imported_at || it?.update_time;
      if (ts && ts > max) max = ts;
    }
    return max || null;
  }, [indicators, prices]);

  const latestForSheet = (sheet) => {
    // CO2 usa "MidDec" invece di "Calendar" come benchmark forward
    const benchmarkPeriod = sheet === "CO2" ? "MidDec" : "Calendar";
    const currentYear = new Date().getFullYear();
    const cals = prices
      .filter((p) => p.source_sheet === sheet && p.period_type === benchmarkPeriod && p.period_year && p.period_year > currentYear && (p.ask != null || p.previous_close != null || p.last_price != null))
      .sort((a, b) => Number(a.period_year) - Number(b.period_year));
    if (cals.length) {
      const c = cals[0];
      // priorità: ASK → CLOSE (previous_close) → LAST
      if (c.ask != null && Number(c.ask) > 0) return { ...c, _displayValue: c.ask, _displayLabel: "ASK" };
      if (c.previous_close != null) return { ...c, _displayValue: c.previous_close, _displayLabel: "CLOSE" };
      if (c.last_price != null) return { ...c, _displayValue: c.last_price, _displayLabel: "LAST" };
      return { ...c, _displayValue: null, _displayLabel: "ASK" };
    }
    const any = prices.filter((p) => p.source_sheet === sheet && p.last_price != null);
    if (!any.length) return null;
    const c = any.sort((a, b) => (a.contract_name > b.contract_name ? 1 : -1))[0];
    return { ...c, _displayValue: c.last_price, _displayLabel: "LAST" };
  };
  const ttfLast = latestForSheet("TTF");
  const psvLast = latestForSheet("PSV");
  const powerLast = latestForSheet("Power");
  const co2Last = latestForSheet("CO2");

  const downloadPdf = async () => {
    try {
      const resp = await api.get("/prices/report/pdf", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Adybo_Prezzi_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error("PDF download failed", e);
    }
  };

  return (
    <div className="space-y-6">
      {user && user.subscription_status && user.subscription_status !== "active" && (
        <div
          className={`card-elevated p-4 border-l-4 ${
            user.subscription_status === "expired"
              ? "!border-l-red-500 bg-red-50/40"
              : "!border-l-amber-400 bg-amber-50/40"
          }`}
          data-testid="subscription-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle
              className={
                user.subscription_status === "expired" ? "text-red-600" : "text-amber-600"
              }
              size={20}
            />
            <div className="flex-1">
              <div
                className={`font-semibold ${
                  user.subscription_status === "expired" ? "text-red-900" : "text-amber-900"
                }`}
              >
                {user.subscription_status === "expired"
                  ? `Abbonamento scaduto${
                      user.subscription_days_left != null
                        ? ` da ${Math.abs(user.subscription_days_left)} giorni`
                        : ""
                    }`
                  : `Il tuo abbonamento scade tra ${user.subscription_days_left} giorni`}
              </div>
              <div
                className={`text-sm mt-1 flex items-center gap-1.5 ${
                  user.subscription_status === "expired" ? "text-red-700" : "text-amber-800"
                }`}
              >
                <Calendar size={12} />
                Scadenza:{" "}
                {user.subscription_end
                  ? fmtDate(user.subscription_end)
                  : "—"}
                {" · "}
                Contatta il tuo referente Adybo per il rinnovo.
              </div>
            </div>
          </div>
        </div>
      )}

      <EaDashboardCard isStaff={isStaffUser} enabled={isStaffUser || !!user?.energy_analytics_enabled} />

      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Panoramica mercati</h1>
          <p className="text-sm text-slate-500 mt-1">Ultimi indicatori energetici e quotazioni forward.</p>
          <div className="mt-2">
            <LastUpdateChip ts={lastPublishedAt} autoRefreshing={refreshing} testid="dashboard-last-update" />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={downloadPdf} className="text-sm brand-gradient text-white rounded-lg px-4 py-2 font-semibold flex items-center gap-2 shadow" data-testid="dashboard-download-report">
            <FileText size={15} /> Report Prezzi PDF
          </button>
          <Link to="/prezzi" className="btn-brand text-sm" data-testid="dashboard-goto-prezzi">
            Vedi tutti i prezzi <ArrowRight size={16} className="inline ml-1" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title={`Forward Gas TTF${ttfLast?.period_year ? ` CAL ${ttfLast.period_year}` : ""}${ttfLast?._displayLabel ? ` · ${ttfLast._displayLabel}` : ""}`} value={ttfLast?._displayValue} change={ttfLast?.change_value} unit="€/MWh" ts={ttfLast?.update_time} testid="kpi-ttf" />
        <KPICard title={`Forward Gas PSV${psvLast?.period_year ? ` CAL ${psvLast.period_year}` : ""}${psvLast?._displayLabel ? ` · ${psvLast._displayLabel}` : ""}`} value={psvLast?._displayValue} change={psvLast?.change_value} unit="€/MWh" ts={psvLast?.update_time} testid="kpi-psv" />
        <KPICard title={`Forward Power${powerLast?.period_year ? ` CAL ${powerLast.period_year}` : ""}${powerLast?._displayLabel ? ` · ${powerLast._displayLabel}` : ""}`} value={powerLast?._displayValue} change={powerLast?.change_value} unit="€/MWh" ts={powerLast?.update_time} testid="kpi-power" />
        <KPICard
          title={
            <>
              Forward CO2
              {co2Last?.period_year && (
                <span className="normal-case"> MidDec {co2Last.period_year}</span>
              )}
              {co2Last?._displayLabel && ` · ${co2Last._displayLabel}`}
            </>
          }
          value={co2Last?._displayValue}
          change={co2Last?.change_value}
          unit="€/tCO2"
          ts={co2Last?.update_time}
          testid="kpi-co2-forward"
        />
        <KPICard title="CO2 EUA Spot" value={co2?.value} change={co2?.change_value} unit="€/tCO2" ts={co2?.update_time || co2?.imported_at} testid="kpi-co2" />
        <KPICard title="Cambio €/$" value={eur?.value} change={eur?.change_value} unit="" ts={eur?.update_time || eur?.imported_at} testid="kpi-eur" digits={3} />
        <KPICard title="Brent Dated" value={brent?.value} change={brent?.change_value} unit="$/bbl" ts={brent?.update_time || brent?.imported_at} testid="kpi-brent" />
        <KPICard title="GME PUN D+1" value={pun?.value} change={pun?.change_value} unit="€/MWh" ts={pun?.update_time || pun?.imported_at} testid="kpi-pun" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
        <PricesMiniCard title="Forward Gas TTF" sheet="TTF" prices={prices} />
        <PricesMiniCard title="Forward Gas PSV" sheet="PSV" prices={prices} />
        <PricesMiniCard title="Forward Power" sheet="Power" prices={prices} />
        <PricesMiniCard title="Forward CO2" sheet="CO2" prices={prices} />
      </div>

      {briefing && (
        <Link to={`/briefing/${briefing.id}`} className="block card-elevated p-6 relative overflow-hidden group" data-testid="dashboard-briefing-card">
          <div className="absolute top-0 right-0 w-64 h-full opacity-10 bg-gradient-to-l from-purple-500 to-transparent pointer-events-none" />
          <div className="flex items-start gap-4 relative">
            <div className="w-12 h-12 rounded-2xl brand-gradient flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-500/30">
              <Sparkles className="text-white" size={22} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest font-bold text-purple-600">Report Mercati Energetici · Ultimo</div>
              <div className="font-display font-bold text-lg text-slate-900 mt-0.5 group-hover:text-blue-600">{briefing.title}</div>
              <p className="text-sm text-slate-500 mt-1 line-clamp-2">{(briefing.content_markdown || "").replace(/[#*]/g, "").slice(0, 220)}</p>
              <div className="text-[11px] text-slate-400 mt-2 font-mono-nums">{fmtDateTime(briefing.generated_at)}</div>
            </div>
            <ArrowRight className="text-slate-400 group-hover:text-blue-600 mt-2" size={20} />
          </div>
        </Link>
      )}

      {news.filter((n) => n.is_featured).length > 0 && (
        <div className="rounded-2xl overflow-hidden border-2 border-amber-300/60 bg-gradient-to-br from-amber-50 via-amber-50/40 to-white shadow-lg shadow-amber-200/30" data-testid="dash-featured-news">
          <div className="px-5 py-2.5 bg-gradient-to-r from-amber-400 to-amber-500 flex items-center gap-2">
            <Star size={14} className="text-white fill-white" />
            <span className="text-xs uppercase tracking-widest font-bold text-white">News in evidenza</span>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {news
              .filter((n) => n.is_featured)
              .slice(0, 4)
              .map((n) => (
                <Link
                  key={n.id}
                  to={`/news/${n.id}`}
                  className="group flex items-start gap-3 -m-2 p-2 rounded-xl hover:bg-white/70 transition-colors"
                  data-testid={`dash-featured-news-${n.id}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0 shadow-md shadow-amber-500/30">
                    <Star size={18} className="text-white fill-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">{n.category || "Generale"} · {fmtDate(n.publication_date)}</div>
                    <div className="font-display font-bold text-slate-900 mt-0.5 group-hover:text-amber-700 leading-snug">{n.title}</div>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">{n.body}</p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-elevated p-5 lg:col-span-1">
          <div className="flex items-center gap-2 mb-3">
            <Newspaper size={16} className="text-blue-600" />
            <div className="font-display font-semibold text-slate-900">Ultime news</div>
          </div>
          <div className="space-y-3">
            {news.filter((n) => !n.is_featured).map((n) => (
              <Link key={n.id} to={`/news/${n.id}`} className="block group" data-testid={`dash-news-${n.id}`}>
                <div className="flex items-start gap-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800 group-hover:text-blue-600 line-clamp-2">{n.title}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{fmtDate(n.publication_date)} · {n.category}</div>
                  </div>
                </div>
              </Link>
            ))}
            {news.filter((n) => !n.is_featured).length === 0 && <div className="text-sm text-slate-400">Nessuna news</div>}
          </div>
        </div>
        <div className="card-elevated p-5 lg:col-span-1">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={16} className="text-purple-600" />
            <div className="font-display font-semibold text-slate-900">Report recenti</div>
          </div>
          <div className="space-y-2">
            {reports.map((r) => {
              const kind = r._kind || "report";
              // Briefing: link interno; report standard/custom: download PDF con auth.
              const url = kind === "briefing"
                ? `/briefing/${r.id}`
                : kind === "custom"
                  ? `${API}/custom-reports/${r.id}/download?auth=${encodeURIComponent(getToken() || "")}`
                  : `${API}/reports/${r.id}/download?auth=${encodeURIComponent(getToken() || "")}&disposition=attachment`;
              const isBriefing = kind === "briefing";
              const isCustom = kind === "custom";
              const hoverBg = isBriefing ? "hover:bg-purple-50/70" : isCustom ? "hover:bg-amber-50/70" : "hover:bg-purple-50/60";
              const iconBg = isBriefing ? "bg-purple-100 group-hover:bg-purple-200" : isCustom ? "bg-amber-100 group-hover:bg-amber-200" : "bg-purple-100 group-hover:bg-purple-200";
              const IconInner = isBriefing
                ? <Sparkles size={13} className="text-purple-600" />
                : isCustom
                  ? <Star size={13} className="fill-amber-500 text-amber-600" />
                  : <Download size={13} className="text-purple-600" />;
              const dateStr = fmtDate(r._sortDate);
              const badgeLabel = isBriefing ? "Mercati Energetici" : isCustom ? "Personalizzato" : (r.category || "");
              const badgeClass = isBriefing
                ? "text-purple-600 bg-purple-100"
                : isCustom
                  ? "text-amber-600 bg-amber-100"
                  : "";
              const commonProps = {
                className: `group flex items-start gap-2 -mx-2 px-2 py-1.5 rounded-lg transition-colors ${hoverBg}`,
                "data-testid": isBriefing
                  ? `dashboard-briefing-item-${r.id}`
                  : isCustom
                    ? `dashboard-custom-report-download-${r.id}`
                    : `dashboard-report-download-${r.id}`,
                title: isBriefing ? "Apri Report Mercati Energetici" : isCustom ? "Report personalizzato per la tua azienda" : undefined,
              };
              const inner = (
                <>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${iconBg}`}>
                    {IconInner}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <div className={`text-sm font-semibold text-slate-800 line-clamp-1 ${isBriefing ? "group-hover:text-purple-700" : isCustom ? "group-hover:text-amber-700" : "group-hover:text-purple-700"}`}>
                        {r.title}
                      </div>
                      {badgeLabel && (
                        <span className={`text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded-full flex-shrink-0 ${badgeClass}`}>
                          {badgeLabel}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {dateStr}
                      {!isBriefing && !isCustom && r.category ? ` · ${r.category}` : ""}
                    </div>
                  </div>
                </>
              );
              return isBriefing ? (
                <Link key={`b-${r.id}`} to={url} {...commonProps}>{inner}</Link>
              ) : (
                <a
                  key={`${isCustom ? "cr" : "r"}-${r.id}`}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={r.filename || `${r.title || "report"}.pdf`}
                  {...commonProps}
                >
                  {inner}
                </a>
              );
            })}
            {reports.length === 0 && <div className="text-sm text-slate-400">Nessun report</div>}
          </div>
        </div>
        <div className="card-elevated p-5 lg:col-span-1">
          <div className="flex items-center gap-2 mb-3">
            <Bell size={16} className="text-orange-500" />
            <div className="font-display font-semibold text-slate-900">Notifiche</div>
          </div>
          <div className="space-y-2">
            {notifs.map((n) => (
              <NotificationItem key={n.id} n={n} onRead={() => setNotifs((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)))} />
            ))}
            {notifs.length === 0 && <div className="text-sm text-slate-400">Nessuna notifica</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
